import { ActivityModel, ActivityGroupModel } from '../../models';
import { ONLINE_QUEST_INDEXNAME } from '../../config/constants';
import Vue from 'vue';

export default {
    namespaced: true,
    state: {
        activeQuests: null,
        draftQuests: null,
        pastQuests: null,
        selectedQuests: [],
        selectionMode: false,
        activityGroups: [],
        activityGroup: null,
        questsFilterQuery: null,
    },
    getters: {
        orderedGroups(state) {
            const groups = [...state.activityGroups];
            return groups.sort((a, b) => {
                if (a.name.toUpperCase() < b.name.toUpperCase()) {
                    return -1;
                } else if (a.name.toUpperCase() > b.name.toUpperCase()) {
                    return 1;
                } else {
                    return 0;
                }
            });
        },
    },
    mutations: {
        setActivityGroup(state, activityGroup) {
            state.activityGroup = activityGroup;
            state.questsFilterQuery = null;
        },
        setQuestsFilterQuery: (state, questsFilterQuery) => state.questsFilterQuery = questsFilterQuery,
        setActivityGroups(state, activityGroups) {
            state.activityGroups = activityGroups;
        },
        setQuests(state, { group, quests }) {
            state[group] = quests;
        },
        setSelectionMode(state, selectionMode) {
            state.selectionMode = selectionMode;
            if (!selectionMode) {
                state.selectedQuests = [];
            }
        },
        setSelectedQuests(state, selectedQuests) {
            state.selectedQuests = selectedQuests;
        },
        removeQuest(state, questId) {
            state.activeQuests = state.activeQuests.filter(quest => quest.id != questId);
            state.draftQuests = state.draftQuests.filter(quest => quest.id != questId);
            state.pastQuests = state.pastQuests.filter(quest => quest.id != questId);
        },
    },
    actions: {
        loadActivityGroup({ commit }, id) {
            return ActivityGroupModel.getOne(id, {
                params: {
                    'response-group': 'org_backend_select_group',
                },
            })
                .then(group => {
                    commit('setActivityGroup', group);
                })
                .catch(() => {
                    commit('setActivityGroup', null);
                });
        },
        loadActivityGroups({ commit, rootState }, signal) {
            return ActivityGroupModel.get({
                signal,
                params: {
                    'response-group': 'org_backend_select_group',
                    limit: -1,
                    'order-by': [
                        {
                            field: 'name',
                            direction: 'ASC',
                            type: 'field',
                        },
                    ],
                    filter: [
                        {
                            type: 'eq',
                            field: 'organization',
                            value: rootState.organization.selectedOrganizationID,
                        },
                        {
                            type: 'isnull',
                            field: 'isHidden',
                        },
                    ],
                },
            })
                .then(r => r.data)
                .then(groups => {
                    commit('setActivityGroups', groups.map(group => {
                        group.activities = group.activities.filter(activity => activity.isHidden !== true);
                        return group;
                    }));
                })
                .catch(() => {
                    commit('setActivityGroups', []);
                });
        },
        createActivityGroup({ state, commit }, body) {
            return ActivityGroupModel.create({
                params: {
                    'response-group': 'org_backend_select_group',
                },
                body,
            }).then(activityGroup => {
                const groups = [...state.activityGroups];
                groups.unshift(activityGroup);
                commit('setActivityGroups', groups);
            });
        },
        updateActivityGroup({ state, commit }, { id, body }) {
            return ActivityGroupModel.patch(id, {
                params: {
                    'response-group': 'org_backend_select_group',
                },
                body,
            }).then(activityGroup => {
                const groups = [...state.activityGroups];
                commit(
                    'setActivityGroups',
                    groups.map(group => {
                        if (group.id == id) {
                            group = {
                                ...activityGroup,
                            };
                        }
                        return group;
                    })
                );
                return activityGroup;
            });
        },
        deleteActivityGroup({ state, commit }, id) {
            return ActivityGroupModel.delete(id, {
                params: {
                    'response-group': 'org_backend_select_group',
                },
            }).then(() => {
                const groups = [...state.activityGroups];
                commit(
                    'setActivityGroups',
                    groups.filter(group => group.id !== id)
                );
            });
        },
        moveQuestToAGroup({ state, commit }, { questId, groupId }) {
            return ActivityModel.patch(questId, {
                params: {
                    'response-group': 'org_backend_dashboard',
                },
                body: {
                    activityGroup: groupId,
                },
            }).then(() => {
                for (const activityGroup of state.activityGroups) {
                    if (activityGroup.id === groupId) {
                        activityGroup.activities.push({id: questId});
                    } else {
                        activityGroup.activities = activityGroup.activities.filter(quest => quest.id != questId);
                    }
                }
                if (state.activityGroup) {
                    commit('removeQuest', questId);
                }
            });
        },
        deleteQuest({ state, commit }, questId) {
            return ActivityModel.delete(questId).then(() => {
                for (const activityGroup of state.activityGroups) {
                    activityGroup.activities = activityGroup.activities.filter(quest => quest.id != questId);
                }
                commit('removeQuest', questId);
            });
        },
        loadQuests({ state, commit, rootState }, { group, filters, orderBy, signal }) {
            if (!state.questsFilterQuery || Vue.$store.state.user.role !== 'Sysadmin') {
                filters.push({
                    type: 'eq',
                    field: 'organization',
                    value: rootState.organization.selectedOrganizationID,
                });
            }

            if (state.questsFilterQuery) {
                filters.push({
                    type: 'like',
                    field: 'name',
                    value: `%${state.questsFilterQuery}%`,
                });
            }

            return ActivityModel.get({
                signal,
                params: {
                    'response-group': 'org_backend_dashboard',
                    filter: filters,
                    'order-by': orderBy,
                    limit: -1,
                },
            })
                .then(quests => {
                    let questCards = [];

                    quests.data.forEach(quest => {
                        questCards.push({
                            ...quest,
                            participantsCount: ActivityModel.countParticipants(quest),
                        });
                    });

                    commit('setQuests', { group, quests: questCards });
                })
                .catch(err => {
                    commit('setQuests', { group, quests: [] });
                    if (err && (!err.code || err.code !== 20)) {
                        throw err;
                    }
                });
        },
        cloneQuest({ commit, state }, { questId, suffix }) {
            return ActivityModel.getOne(questId, {
                params: {
                    'response-group': 'activity_clone',
                },
            })
                .then(quest => {
                    let questsList = [];

                    if (state.activeQuests) {
                        questsList = questsList.concat(state.activeQuests);
                    }
                    if (state.draftQuests) {
                        questsList = questsList.concat(state.draftQuests);
                    }
                    if (state.pastQuests) {
                        questsList = questsList.concat(state.pastQuests);
                    }

                    delete quest.id;

                    quest.isActive = false;
                    quest.name = getClonedQuestName(quest.name, questsList, suffix);
                    quest.participantActivities = [];

                    if (quest.activityDates) {
                        const activityDates = [];
                        for (const activityDate of quest.activityDates) {
                            activityDates.push({
                                date: activityDate.date.date,
                                startTime: activityDate.startTime,
                                endTime: activityDate.endTime,
                                participants: [],
                                isActive: activityDate.isActive,
                            });
                        }
                        quest.activityDates = activityDates;
                    }

                    const responsibles = [];
                    for (const responsible of quest.responsibleActivities) {
                        responsibles.push({
                            participant: responsible.participant.id,
                            receivesPoints: responsible.receivesPoints,
                        });
                    }
                    quest.responsibleActivities = responsibles;

                    const experiences = [];
                    for (const experience of quest.experiences) {
                        experiences.push(experience.id);
                    }
                    quest.experiences = experiences;
                    quest.pointsApplied = null;

                    if (quest.activityType.indexname === ONLINE_QUEST_INDEXNAME) {
                        quest.activityOnline = {
                            hash: null,
                            points: 0,
                        };
                    }

                    quest.startDate = quest.startDate.date;
                    quest.endDate = quest.endDate.date;

                    return quest;
                })
                .then(quest => {
                    if (quest.activityType.indexname === ONLINE_QUEST_INDEXNAME) {
                        return ActivityModel.createOnlineQuestHash()
                            .then(hash => {
                                quest.activityOnline.hash = hash;
                                return quest;
                            });
                    } else {
                        return quest;
                    }
                })
                .then(quest => {
                    return ActivityModel.create({
                        params: {
                            'response-group': 'org_backend_dashboard',
                        },
                        body: quest,
                    })
                        .then(clonedQuest => {
                            return ActivityModel.getOne(clonedQuest.id, {
                                params: {
                                    'response-group': 'org_backend_dashboard',
                                },
                            });
                        })
                        .then(clonedQuest => {
                            const quests = [...state.draftQuests];
                            quests.push(clonedQuest);
                            quests.sort((a, b) => {
                                if (a.startDate.date < b.startDate.date) {
                                    return 1;
                                } else if (a.startDate.date > b.startDate.date) {
                                    return -1;
                                } else {
                                    return 0;
                                }
                            });
                            commit('setQuests', { group: 'draftQuests', quests });
                            for (const activityGroup of state.activityGroups) {
                                if (activityGroup.id === clonedQuest.activityGroup.id) {
                                    activityGroup.activities.push({id: clonedQuest.id});
                                }
                            }
                        })
                        .catch(() => {
                            // TODO: display error notification
                        });
                });
        },
        activateQuest({}, questId) {
            return ActivityModel.patch(questId, {
                body: {
                    isActive: true,
                },
                params: {
                    'response-group': 'org_backend_quest_details',
                },
            })
                .catch(() => {
                    // TODO: show error notification
                });
        },
        deactivateQuest({ dispatch }, questId) {
            return ActivityModel.changeQuestStatus(questId, false)
                .then(quest => {
                    dispatch('addQuestToGroup', {quest, group: 'draftQuests'});
                })
                .catch(() => {
                    // TODO: show error notification
                });
        },
        addQuestToGroup({commit, state}, {quest, group}) {
            if (state[group]) {
                const quests = [...state[group]];
                quests.push(quest);
                quests.sort((a, b) => {
                    if (a.startDate.date < b.startDate.date) {
                        return 1;
                    } else if (a.startDate.date > b.startDate.date) {
                        return -1;
                    } else {
                        return 0;
                    }
                });
                commit('removeQuest', quest.id);
                commit('setQuests', { group, quests });
            }
        },
    },
};

const getClonedQuestName = (questName, quests, suffix) => {
    let counter = 1;

    if (questName.search(suffix) === -1) {
        questName = `${questName} ${suffix} `;
    } else {
        counter = parseInt(questName.slice(-1)) + 1;
        questName = questName.slice(0, -1);
    }
    let continueCycle = true;
    while (continueCycle) {
        let foundRecord = false;
        quests.some(quest => {
            if (quest.name === `${questName}${counter}`) {
                foundRecord = quest;
            }
        });
        if (foundRecord) {
            counter++;
        } else {
            questName = `${questName}${counter}`;
            continueCycle = false;
        }
    }
    return questName;
};
